import React, { useCallback } from 'react';
import { quoteAPI } from 'modules/quote';
import { useMutation } from '@apollo/client';
import { useEquipmentActions } from 'modules/equipment';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { TradeInListing } from './TradeInListing';
import { TradeInEmptyState } from './TradeInEmptyState';
import { DealBuilderFormValues } from '../../../../types';
import { EMPTY_TRADE_IN_ROW } from '../../../../constants';
import { useDealBuilderContext } from '../../../../context';
import { dealBuilderApi, UpdateDealBuilderQuoteResponse, UpdateDealBuilderQuoteVariables } from '../../../../api';

export const TradeInSection = () => {
  const { control, getValues } = useFormContext<DealBuilderFormValues>();
  const { fields, append, remove } = useFieldArray({ control, name: 'pricing.tradeIn' });

  const { onDeleteEquipment } = useEquipmentActions();

  const {
    activeEntity,
    setActiveEntity,
    sourceData: { voId, dynamicsVOId },
  } = useDealBuilderContext();

  const [updateDealBuilderQuoteMutation] = useMutation<UpdateDealBuilderQuoteResponse, UpdateDealBuilderQuoteVariables>(
    dealBuilderApi.updateDealBuilderQuote,
    {
      awaitRefetchQueries: true,
      refetchQueries: [quoteAPI.getQuoteCalcById],
    }
  );

  const persistQuoteTradeIns = useCallback(async () => {
    const quoteId = activeEntity?.quote?.quoteId;

    if (!quoteId) return;

    const rows = getValues('pricing.tradeIn').filter(row => row.equipmentId);

    const result = await updateDealBuilderQuoteMutation({
      variables: {
        quoteId,
        input: {
          vendorOpportunityId: voId,
          equipmentTradeIns: rows.map(row => ({
            equipmentId: row.equipmentId,
            vin: row.serialNumberVin || undefined,
            tradeInDetails: {
              tradeValue: row.tradeInValue ?? 0,
              tradeAmountOwed: row.amountOwed ?? 0,
              actualCashValue: row.actualCashValue ?? 0,
              overUnderAllowance: row.overUnderAllowance ?? 0,
              tradePayoffLienholder: row.payoffLienholder || undefined,
              tradePayoffAccountNumber: row.payoffAccountNumber || undefined,
              tradePayoffGoodThroughDate: row.payoffGoodThroughDate || undefined,
            },
          })),
        },
      },
    });

    const updatedQuote = result.data?.dealBuilder?.updateDealBuilderQuote?.entity;

    if (updatedQuote && activeEntity) {
      setActiveEntity({ ...activeEntity, quote: updatedQuote });
    }
  }, [activeEntity, getValues, updateDealBuilderQuoteMutation, setActiveEntity, voId]);

  const handleAddRow = () => append(EMPTY_TRADE_IN_ROW);

  const handleRemoveRow = (rowIndex: number) => {
    const row = getValues(`pricing.tradeIn.${rowIndex}`);

    if (row.equipmentId) {
      // onDeleteEquipment shows its own confirmation dialog before deleting
      onDeleteEquipment(
        { equipmentId: row.equipmentId, vendorOpportunityId: voId, dynamicsVendorOpportunityId: dynamicsVOId },
        async () => {
          remove(rowIndex);
          await persistQuoteTradeIns();
        }
      );
    } else {
      remove(rowIndex);
    }
  };

  return (
    <RenderWithCondition condition={!!fields.length} fallBack={<TradeInEmptyState onAdd={handleAddRow} />}>
      <TradeInListing fields={fields} onAddRow={handleAddRow} onRemoveRow={handleRemoveRow} />
    </RenderWithCondition>
  );
};
